import { Injectable } from "@angular/core";
import { WalletAppCommand } from "@fabric-types/app-wallet";
import {
	CacheFactory,
	CacheStore,
	DocumentRef,
	CurrencyConfigService as GlobalCurrencyConfigService,
	StorageMode,
} from "@obg/ngx.global";
import { find, map as lodashMap, uniq } from "lodash-es";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { CoreHttpClient } from "../http-client/http-client.service";

import { CurrencyConfigService as CommonCurrencyConfigService } from "./currency.config";
import { Currency } from "./currency.model";

const CURRENCY_CACHE_KEY = "currencyCode";
const CACHE_STORE_KEY = "common.currency";
const ERROR_CODE = "MISSING_CURRENCY_CODE";

/**
 * @deprecated -[fabric-deprecated] - Use currency-service from app-wallet instead.
 * */
@Injectable({
	providedIn: "root",
})
export class CurrencyService {
	private cache: CacheStore;

	constructor(
		private cacheFactory: CacheFactory,
		private globalConfig: GlobalCurrencyConfigService,
		private commonConfig: CommonCurrencyConfigService,
		private httpClient: CoreHttpClient,
		private documentRef: DocumentRef
	) {
		this.cache = this.cacheFactory.get(CACHE_STORE_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: Number.MAX_VALUE,
		});
	}

	/**
	 * Initializes the currency code configuration by retrieving the latest currency code used from cache.
	 * If there is no data in the cache, then it falls back to the one passed as input parameter.
	 * @param fallbackCurrencyCode - The fall back currency code to use when cache currency code is not available.
	 */
	initialize(fallbackCurrencyCode: string): Observable<void> {
		return this.cache.getItemAsObservable<string>(CURRENCY_CACHE_KEY).pipe(
			map((currencyFromCache) => {
				const currencyCode = currencyFromCache || fallbackCurrencyCode;
				if (!currencyCode) {
					throw new Error(ERROR_CODE);
				}

				this.globalConfig.set({ currencyCode });
				this.documentRef.obgEventBus?.dispatch(WalletAppCommand.SetCurrentCurrencyCode, {
					currencyCode,
				});
			})
		);
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppCommand.SetCurrentCurrencyCode instead
	 */
	set(currencyCode: string): Observable<string> {
		if (!currencyCode) {
			throw new Error(ERROR_CODE);
		}
		this.globalConfig.set({
			currencyCode,
		});
		this.documentRef.obgEventBus?.dispatch(WalletAppCommand.SetCurrentCurrencyCode, {
			currencyCode,
		});
		return this.cache.setItemAsObservable(CURRENCY_CACHE_KEY, currencyCode);
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppValue.AllCurrencies instead
	 */
	getCurrencies(): Observable<Currency[]> {
		return this.httpClient.get<Currency[]>({ url: this.commonConfig.getCurrenciesEndpoint() });
	}
	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppValue.CurrencyByCurrencyCode instead
	 *
	 * Returns a currency by its code (e.g.: EUR, GBP, SEK).
	 */
	getByCode(currencies: Currency[], currencyCode: string): Currency | undefined {
		return find(currencies, (currency) => currency.code === currencyCode);
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppValue.CurrencyByCountryCode instead
	 *
	 * Returns a currency to the given country code (e.g.: MT, UK, SE).
	 */

	getByCountryCode(currencies: Currency[], countryCode: string): Currency | undefined {
		return this.getByCode(currencies, this.getCurrencyCodeByCountryCode(countryCode));
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppValue.CurrencyByCountryCodes instead
	 *
	 * Returns currencies to the given set of country codes (e.g.: [MT, UK, SE]).
	 */
	getByCountryCodes(currencies: Currency[], countryCodes: string[]): Currency[] {
		const currencyCodes = uniq(
			lodashMap(countryCodes, (countryCode) => this.getCurrencyCodeByCountryCode(countryCode))
		);

		const matchingCurrencies: Currency[] = [];
		for (const currency of currencies) {
			if (currencyCodes.indexOf(currency.code) > -1) {
				matchingCurrencies.push(currency);
			}
			if (matchingCurrencies.length === currencyCodes.length) {
				break;
			}
		}
		return matchingCurrencies;
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use WalletAppValue.CurrencyByCurrencyCode instead
	 *
	 * Returns a currency code (e.g.: EUR, GBP, SEK) to the given country code (e.g.: MT, UK, SE).
	 */
	getCurrencyCodeByCountryCode(countryCode: string): string {
		// todo: ideally this mapping should come from an API, instead of site level configuration
		return (
			this.commonConfig.getCurrencyCodesByCountryCode()[countryCode] || this.commonConfig.getDefaultCurrencyCode()
		);
	}
}
